import * as React from 'react';
import { useState, useCallback } from 'react';
import { Button, Modal, Row, Space, Table } from 'antd';
import { IWidget } from 'common/schema/widget/Widget';
import { MetadataLogic } from 'common/schema/metadata/MetadataLogic';
import {
  EditOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  MinusOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { fromPairs } from 'lodash';
import { ColumnProps } from 'antd/lib/table/Column';
import { kebabCase } from 'lodash';
import { FormWidgetItem } from './wrapper/FormWidgetItem';
import './FormWidgetInputListView.scss';
import { FormNestedWidgetModal } from './nested/FormNestedWidgetModal';
import { useWidgetListCompile } from './nested/useWidgetListCompile';
import { IRequestListRow } from 'common/schema/request/Request';
import { WidgetList } from 'common/schema/widget/WidgetList';
import { formatMetadataValue } from 'common/schema/metadata/formatMetadataValue';
import { WidgetLogic } from 'common/schema/widget/WidgetLogic';

export interface FormWidgetInputListViewProps {
  widget: IWidget;
}

/**
 * Interfaccia per la compilazione degli input di tipo lista
 */
export function FormWidgetInputListView(props: FormWidgetInputListViewProps) {
  const { widget } = props;

  // Modal vista dati lista FullScreen
  const [tableExpandedModal, setTableExpandedModal] = useState(false);
  const showExpandedModal = () => {
    setTableExpandedModal(true);
  };
  const closeExpandedModal = () => {
    setTableExpandedModal(false);
  };

  const list = useWidgetListCompile(widget);

  // Indice della riga selezionata
  const [selected, setSelected] = useState<IRequestListRow | undefined>(
    undefined
  );

  // Abbiamo un singolo metadato per i campi di testo
  const metadata = MetadataLogic.singleFromWidget(widget);
  const name = `values.${metadata.id}`;

  const subwidgets = widget.input?.subwidgets ?? [];
  const submetadata = WidgetList.nestedMetadata(widget);

  // Creiamo le colonne per ogni input ( input label )
  // TODO: gestire colonne che non devono essere mostrate
  const columns: ColumnProps<IRequestListRow>[] = [
    ...submetadata.map(m => ({
      // Per il subwidget il title è la label ma in caso non sia presente (semplice fault tollerance) è il nome del metadato
      title:
        WidgetLogic.getWidgetLabel(
          MetadataLogic.getWidget(subwidgets, m.id).widget!
        ) ?? m.name,
      dataIndex: ['values', m.id],
      key: m.id,
      render: (value: any) => formatMetadataValue(m, value)
    })),
    {
      title: !tableExpandedModal && (
        <div className="expand-buttons">
          <Button
            onClick={showExpandedModal}
            // disabled={tableExpandedModal}
            size="small"
            icon={<FullscreenOutlined />}
          />
        </div>
      ),
      key: 'actions',
      width: 72,
      fixed: 'right' as const,
      render: (_, row) => (
        <Space size={6} className="actions-buttons">
          <Button
            size="small"
            type="primary"
            icon={<EditOutlined />}
            onClick={() => setSelected(row)}
          />
          <Button
            size="small"
            type="default"
            icon={<MinusOutlined />}
            onClick={() => list.removeRow(row)}
          />
        </Space>
      )
    }
  ];

  // determiniamo la larghezza massima della tabella per lo scrool orizzontale
  const size = 150 * subwidgets.length;

  const renderTable = (
    <Table
      size="small"
      bordered
      footer={() => (
        <Button
          type="primary"
          onClick={() => {
            const row = list.addRow();
            setSelected(row);
          }}
          icon={<PlusOutlined />}
        >
          Aggiungi
        </Button>
      )}
      className="form-widget-input-list-view-table"
      dataSource={list.rows}
      columns={columns}
      pagination={false}
      scroll={{ x: size }}
    />
  );

  return (
    <FormWidgetItem name={name} widget={widget}>
      <>
        {renderTable}
        <Modal
          bodyStyle={{ padding: '0' }}
          closeIcon={<FullscreenExitOutlined />}
          title={widget.input?.label}
          visible={tableExpandedModal}
          width={'96%'}
          onCancel={closeExpandedModal}
          footer={false}
        >
          {renderTable}
        </Modal>
        <FormNestedWidgetModal
          row={selected}
          widget={widget}
          onClose={() => setSelected(undefined)}
        />
      </>
    </FormWidgetItem>
  );
}
